import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { menuService } from '../services/api';
import logo from '../logo.png';

const HomePage = () => {
  const { isAuthenticated, getUserName } = useAuth();
  const { formatPrice } = useCurrency();
  const [featuredItems, setFeaturedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    createPearls();
    fetchFeaturedItems();
    
    return () => {
      // Cleanup pearls on unmount
      const existingPearls = document.querySelectorAll('.pearl');
      existingPearls.forEach(pearl => pearl.remove());
    };
  }, []);

  // Function to create and place pearls randomly (maintaining original animation)
  const createPearls = () => {
    const numPearls = 30; // Reduced for better performance
    const body = document.body;
    const colors = [
      '#f472b6', '#a855f7', '#fde047', '#86efac', '#67e8f9',
      '#374151', '#c084fc', '#fb923c', '#60a5fa', '#4ade80', '#f9a8d4'
    ];
    const sizes = ['sm', 'md', 'lg'];

    // Clear any existing pearls first
    const existingPearls = document.querySelectorAll('.pearl');
    existingPearls.forEach(pearl => pearl.remove());

    for (let i = 0; i < numPearls; i++) {
      const pearl = document.createElement('div');
      const sizeClass = `pearl-${sizes[Math.floor(Math.random() * sizes.length)]}`;
      pearl.className = `pearl ${sizeClass}`;

      pearl.style.left = `${Math.random() * 100}vw`;
      pearl.style.top = `${Math.random() * 100}vh`;
      pearl.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      pearl.style.animationDelay = `${Math.random() * 5}s`;
      pearl.style.animationDuration = `${5 + Math.random() * 10}s`;

      body.appendChild(pearl);
    }
  };

  const fetchFeaturedItems = async () => {
    try {
      const response = await menuService.getAllItems();
      if (response.data && response.data.success) {
        // Get first 3 items as featured
        const items = response.data.data.slice(0, 3);
        setFeaturedItems(items);
      }
    } catch (error) {
      console.error('Error fetching featured items:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <img 
            src={logo} 
            alt="Bunny Boba Logo" 
            className="bunny-logo"
          />
          
          <h2 className="hero-title">
            {isAuthenticated ? `Welcome Back, ${getUserName()}!` : 'Welcome to Bunny Boba!'}
          </h2>
          
          <p className="hero-subtitle">
            Experience the perfect blend of flavors, fun, and freshness. 
            From classic milk teas to innovative fruit combinations.
          </p>
          
          <div className="hero-actions">
            <div className="hero-buttons">
              <Link to="/menu" className="btn btn-primary">
                Explore Menu
              </Link>
              {!isAuthenticated && (
                <Link to="/auth" className="btn btn-secondary">
                  Login / Sign Up
                </Link>
              )}
            </div>
            {!isAuthenticated && (
              <p className="auth-hint">
                Login to start ordering your favorite drinks!
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Featured Menu Items */}
      <section className="featured-section">
        <div className="container">
          <h3 className="section-title">Featured Drinks</h3>
          
          {loading ? (
            <div className="loading">
              <p>Loading our delicious drinks...</p>
            </div>
          ) : (
            <div className="featured-grid">
              {featuredItems.length > 0 ? (
                featuredItems.map(item => (
                  <div key={item.id} className="featured-item">
                    <div className="item-image-placeholder">
                      {item.imageUrl && item.imageUrl.trim() !== '' ? (
                        <img 
                          src={item.imageUrl} 
                          alt={item.name}
                          className="menu-item-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                      ) : null}
                      <span className="fallback-emoji" style={{display: (item.imageUrl && item.imageUrl.trim() !== '') ? 'none' : 'block'}}>
                        🚫📷
                      </span>
                    </div>
                    <h4>{item.name}</h4>
                    <p className="item-description">{item.description}</p>
                    <p className="item-price">{formatPrice(item.price)}</p>
                    <Link 
                      to={`/drink/${item.id}`} 
                      className="btn btn-primary btn-small"
                    >
                      View Details
                    </Link>
                  </div>
                ))
              ) : (
                <div className="no-items">
                  <p>Our delicious menu is being prepared! Check back soon.</p>
                </div>
              )}
            </div>
          )}
          
          <div className="center">
            <Link to="/menu" className="btn btn-primary">
              View Full Menu
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <div className="feature-grid">
            <div className="feature">
              <div className="feature-icon">🧋</div>
              <h3>Premium Ingredients</h3>
              <p>We use only the finest ingredients to craft authentic and delicious boba tea</p>
            </div>
            {/* <div className="feature">
              <div className="feature-icon">🌟</div>
              <h3>Fresh Daily</h3>
              <p>Our pearls and toppings are made fresh daily for the perfect texture and taste</p>
            </div>*/
            <div className="feature">
              <div className="feature-icon">📱</div>
              <h3>Easy Ordering</h3>
              <p>Order online for quick pickup or enjoy browsing our menu anytime</p>
            </div> }
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 